"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
const ethers_1 = require("ethers");
const signature_1 = require("../../signature");
const utils_1 = require("./utils");
class EVM {
    /**
     * Constructs an instance of the EVM class with specified configuration.
     *
     * @param {Object} config - The configuration object for the EVM instance.
     * @param {string} config.providerUrl - The URL of the Ethereum JSON RPC provider.
     * @param {string} config.relayerUrl - The URL of the relayer service.
     * @param {ChainSignatureContracts} config.contract - The contract identifier for chain signature operations.
     */
    constructor(config) {
        this.provider = new ethers_1.ethers.JsonRpcProvider(config.providerUrl);
        this.relayerUrl = config.relayerUrl;
        this.contract = config.contract;
    }
    /**
     * Prepares a transaction object for signature by serializing and hashing it.
     *
     * @param {object} transaction - The transaction object to prepare.
     * @returns {string} The hashed transaction ready for signature.
     */
    static prepareTransactionForSignature(transaction) {
        const serializedTransaction = ethers_1.ethers.Transaction.from(transaction).unsignedSerialized;
        const transactionHash = (0, ethers_1.keccak256)(serializedTransaction);
        return transactionHash;
    }
    /**
     * Sends a signed transaction to the network for execution.
     *
     * @param {ethers.TransactionLike} transaction - The transaction object to be sent.
     * @param {ethers.SignatureLike} signature - The signature object associated with the transaction.
     * @returns {Promise<ethers.TransactionResponse>} A promise that resolves to the response of the executed transaction.
     */
    sendSignedTransaction(transaction, signature) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const serializedTransaction = ethers_1.ethers.Transaction.from(Object.assign(Object.assign({}, transaction), { signature })).serialized;
                return yield this.provider.broadcastTransaction(serializedTransaction);
            }
            catch (error) {
                console.error('Transaction execution failed:', error);
                throw new Error('Failed to send signed transaction.');
            }
        });
    }
    /**
     * Enhances a transaction with current gas price, estimated gas limit, chain ID, and nonce.
     *
     * This method fetches the current gas price, estimates the gas limit required for the transaction, and retrieves the nonce for the transaction sender's address.
     * It then returns a new transaction object that includes the original transaction details along with the fetched gas price, estimated gas limit, the chain ID of the EVM object, and the nonce.
     *
     * @param {ethers.providers.TransactionRequest} transaction - The initial transaction object without gas details or nonce.
     * @returns {Promise<ethers.providers.TransactionRequest>} A new transaction object augmented with gas price, gas limit, chain ID, and nonce.
     */
    attachGasAndNonce(transaction) {
        return __awaiter(this, void 0, void 0, function* () {
            const hasUserProvidedGas = transaction.gasLimit &&
                transaction.maxFeePerGas &&
                transaction.maxPriorityFeePerGas;
            const { gasLimit, maxFeePerGas, maxPriorityFeePerGas } = hasUserProvidedGas
                ? transaction
                : yield (0, utils_1.fetchEVMFeeProperties)(this.provider._getConnection().url, transaction);
            const nonce = yield this.provider.getTransactionCount(transaction.from, 'latest');
            const { from } = transaction, rest = __rest(transaction, ["from"]);
            return Object.assign({ gasLimit,
                maxFeePerGas,
                maxPriorityFeePerGas, chainId: this.provider._network.chainId, nonce, type: 2 }, rest);
        });
    }
    /**
     * Fetches the balance of the given EVM address.
     *
     * This method uses the current provider to query the balance of the specified address.
     * The balance is returned in ethers as a string.
     *
     * @param {string} address - The EVM address to fetch the balance for.
     * @returns {Promise<string>} The balance of the address in ethers.
     */
    getBalance(address) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const balance = yield this.provider.getBalance(address);
                return ethers_1.ethers.formatEther(balance);
            }
            catch (error) {
                console.error(`Failed to fetch balance for address ${address}:`, error);
                throw new Error('Failed to fetch balance.');
            }
        });
    }
    /**
     * Manages the lifecycle of an EVM transaction, encompassing preparation, signing, and broadcasting. This method calculates gas and nonce, digitally signs the transaction using on-chain mechanisms, and broadcasts it for execution.
     *
     * The digital signing process is detailed in @signature.ts, involving the signing of a transaction hash with the derivation path.
     *
     * @param {Transaction} data - Contains the transaction details such as the recipient's address and the amount to be transferred.
     * @param {NearAuthentication} nearAuthentication - The NEAR accountId, keypair and networkId used for signing the transaction.
     * @param {string} path - The derivation path utilized for the signing of the transaction.
     * @returns {Promise<ethers.TransactionResponse | undefined>} A promise that resolves to the response of the executed transaction, or undefined if the transaction fails to execute.
     */
    handleTransaction(data, nearAuthentication, path) {
        return __awaiter(this, void 0, void 0, function* () {
            const derivedFrom = yield (0, utils_1.fetchDerivedEVMAddress)({
                signerId: nearAuthentication.accountId,
                path,
                nearNetworkId: nearAuthentication.networkId,
                multichainContractId: this.contract,
            });
            if (data.from && data.from.toLowerCase() !== derivedFrom.toLowerCase()) {
                throw new Error('Provided "from" address does not match the derived address');
            }
            const from = data.from || derivedFrom;
            const transaction = yield this.attachGasAndNonce(Object.assign(Object.assign({}, data), { from }));
            const transactionHash = EVM.prepareTransactionForSignature(transaction);
            const signature = yield (0, signature_1.sign)({
                transactionHash,
                path,
                nearAuthentication,
                contract: this.contract,
                relayerUrl: this.relayerUrl,
            });
            if (signature) {
                const r = `0x${signature.r}`;
                const s = `0x${signature.s}`;
                const v = signature.v;
                const transactionResponse = yield this.sendSignedTransaction(transaction, ethers_1.ethers.Signature.from({ r, s, v }));
                return transactionResponse;
            }
            return undefined;
        });
    }
}
exports.default = EVM;
